import type {
  AgentCoreSettings,
  AgentPlatformStatus,
  CopilotStudioSettings
} from "@/api/useDeployments.types"
import type {
  AgentCoreCodeAccessInput,
  ConnectionStatus
} from "./useAgentPlatform.types"

// The AWS regions AgentCore runs in
export const AGENTCORE_REGIONS = [
  "us-east-1",
  "us-east-2",
  "us-west-2",
  "ca-central-1",
  "sa-east-1",
  "eu-central-1",
  "eu-west-1",
  "eu-west-2",
  "eu-west-3",
  "eu-north-1",
  "ap-south-1",
  "ap-southeast-1",
  "ap-southeast-2",
  "ap-northeast-1",
  "ap-northeast-2"
] as const

export const AGENTCORE_TEMPLATE_FILENAME = "kodem-agentcore.yaml"

export const AGENTCORE_STACK_NAME = "kodem-agentcore"

// The template parameters the user sets (api-agents
// internal/platforms/agentcore/template.yaml), by the code access field that
// sets each; Kodem's own parameters take the new template's defaults, which
// may be new or changed
const AGENTCORE_USER_PARAMETERS = {
  buckets: "KodemAgentCodeBuckets",
  kmsKeyArns: "KodemAgentCodeKmsKeyArns"
} as const

// The S3 buckets and KMS keys of the agents deployed from a code upload, as
// typed: a comma or space separated list each, empty to leave the stack's
export interface AgentCoreCodeAccess {
  buckets: string
  kmsKeyArns: string
}

export const EMPTY_AGENTCORE_CODE_ACCESS: AgentCoreCodeAccess = {
  buckets: "",
  kmsKeyArns: ""
}

export const DEFAULT_AGENTCORE_CODE_ACCESS_INPUT: AgentCoreCodeAccessInput = {
  codeUpload: false,
  access: EMPTY_AGENTCORE_CODE_ACCESS
}

// Switched off, the stack gets no code access: what was typed in stays for switching back on
export const getStackCodeAccess = ({
  codeUpload,
  access
}: AgentCoreCodeAccessInput) =>
  codeUpload ? access : EMPTY_AGENTCORE_CODE_ACCESS

// S3's bucket naming rules, the ones a pattern can tell
const BUCKET_PATTERN = /^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/

// Any partition: aws, aws-us-gov, aws-cn
const KMS_KEY_ARN_PATTERN = /^arn:aws[a-z-]*:kms:[a-z0-9-]+:\d{12}:key\/[\w-]+$/

const CODE_ACCESS_PATTERNS: Record<keyof AgentCoreCodeAccess, RegExp> = {
  buckets: BUCKET_PATTERN,
  kmsKeyArns: KMS_KEY_ARN_PATTERN
}

// A code access field's values, deduplicated in the order typed
export const getCodeAccessValues = (value: string) => [
  ...new Set(value.split(/[\s,]+/).filter(Boolean))
]

// The field's values that are not a bucket name or a KMS key ARN
export const getInvalidCodeAccessValues = (
  field: keyof AgentCoreCodeAccess,
  value: string
) =>
  getCodeAccessValues(value).filter(
    (item) => !CODE_ACCESS_PATTERNS[field].test(item)
  )

export const isAgentCoreCodeAccessValid = (access: AgentCoreCodeAccess) =>
  (Object.keys(CODE_ACCESS_PATTERNS) as (keyof AgentCoreCodeAccess)[]).every(
    (field) => getInvalidCodeAccessValues(field, access[field]).length === 0
  )

// Deploys the downloaded template in each selected region: creates the stack
// where it is not deployed yet, and updates it where it is. A code access
// field that is filled in sets its parameter; an empty one keeps what the
// stack has (none on a new stack). The values are validated, so quoting them
// only keeps their commas from splitting the AWS CLI's shorthand. The stack
// names its IAM roles. One line, as the command box shows it.
export const getAgentCoreStackCommand = (
  regions: string[],
  access: AgentCoreCodeAccess = EMPTY_AGENTCORE_CODE_ACCESS
) => {
  const stack = `--region "$region" --stack-name ${AGENTCORE_STACK_NAME} --template-body file://${AGENTCORE_TEMPLATE_FILENAME} --capabilities CAPABILITY_NAMED_IAM`
  const parameters = (
    Object.entries(AGENTCORE_USER_PARAMETERS) as [
      keyof AgentCoreCodeAccess,
      string
    ][]
  ).map(([field, key]) => {
    const values = getCodeAccessValues(access[field]).join(",")

    return {
      set: values && `'ParameterKey=${key},ParameterValue="${values}"'`,
      kept: `ParameterKey=${key},UsePreviousValue=true`
    }
  })
  const updated = parameters.map(({ set, kept }) => set || kept).join(" ")
  const created = parameters
    .map(({ set }) => set)
    .filter(Boolean)
    .join(" ")

  return [
    `for region in ${regions.join(" ")}; do`,
    `if aws cloudformation describe-stacks --region "$region" --stack-name ${AGENTCORE_STACK_NAME} >/dev/null 2>&1;`,
    `then aws cloudformation update-stack ${stack} --parameters ${updated};`,
    `else aws cloudformation create-stack ${stack}${created && ` --parameters ${created}`};`,
    "fi; done"
  ].join(" ")
}

// The command's values, which the user fills in from the AgentCore console
const TAG_PLACEHOLDERS = {
  region: "<region>",
  gatewayArn: "<gateway-arn>",
  roleName: "<role-name>"
}

// The key ends at the first "=": the value may hold more
const splitControlTag = (tag: string) => {
  const controlTag = tag.trim()
  const split = controlTag.indexOf("=")

  return {
    controlTag,
    key: controlTag.slice(0, split),
    value: controlTag.slice(split + 1)
  }
}

// Tags a gateway with the control tag (key=value), once the tag is valid. The
// CLI prints nothing on success, so the command says what it tagged
export const getGatewayTagCommand = (tag: string) => {
  if (!isRoleTag(tag)) return undefined

  const { controlTag } = splitControlTag(tag)
  const { region, gatewayArn } = TAG_PLACEHOLDERS

  // tag-resource calls the CLI's default region unless told the gateway's
  return `aws bedrock-agentcore-control tag-resource --region ${region} --resource-arn ${gatewayArn} --tags ${controlTag} && echo "Tagged the gateway with ${controlTag}"`
}

// Tags a role (a gateway's, or an agent's Kodem may block) with the control tag
export const getRoleTagCommand = (tag: string) => {
  if (!isRoleTag(tag)) return undefined

  const { controlTag, key, value } = splitControlTag(tag)
  const { roleName } = TAG_PLACEHOLDERS

  return `aws iam tag-role --role-name ${roleName} --tags Key=${key},Value=${value} && echo "Tagged role ${roleName} with ${controlTag}"`
}

export const DEFAULT_AGENTCORE_SETTINGS: AgentCoreSettings = {
  regions: ["us-east-1"],
  eventDiscovery: true,
  controlEnabled: false,
  // Control needs a tag: offering it filled in keeps Connect from staying disabled on an empty field
  controlledRoleTag: "kodem-controlled=true"
}

export const DEFAULT_COPILOT_STUDIO_SETTINGS: CopilotStudioSettings = {
  environmentIds: []
}

// Any partition: aws, aws-us-gov, aws-cn
// The role name (IAM's characters, at most 64) may follow a path, e.g. service-role/
const ROLE_ARN_PATTERN =
  /^arn:aws[a-z-]*:iam::\d{12}:role\/(?:[\w+=,.@-]+\/)*[\w+=,.@-]{1,64}$/

// The characters AWS allows in tags, without spaces: none a shell would
// expand, so the tag goes into a command unquoted. The value may hold "="
const ROLE_TAG_PATTERN = /^[\p{L}\p{N}_.:/+@-]+=[\p{L}\p{N}_.:/=+@-]*$/u

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const isRoleArn = (value: string | undefined) =>
  ROLE_ARN_PATTERN.test((value || "").trim())

export const isRoleTag = (value: string | undefined) =>
  ROLE_TAG_PATTERN.test((value || "").trim())

// Entra tenant and application (client) ids
export const isGuid = (value: string | undefined) =>
  GUID_PATTERN.test((value || "").trim())

const ENTRA_LOGIN_URL = "https://login.microsoftonline.com"

// Copilot Studio's app as the subject of its federated tokens names it
const COPILOT_STUDIO_APP = "m1WPnYRZpEaQKq1Cceg--g"

const FEDERATED_AUDIENCE = "api://AzureADTokenExchange"

const toBase64Url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "")

// A GUID's 16 bytes in Microsoft's order (.NET Guid.ToByteArray): the first
// three groups little-endian
const getGuidBytes = (guid: string) => {
  const bytes = (guid.replace(/-/g, "").match(/../g) || []).map((hex) =>
    parseInt(hex, 16)
  )

  return Uint8Array.from([
    ...bytes.slice(0, 4).reverse(),
    ...bytes.slice(4, 6).reverse(),
    ...bytes.slice(6, 8).reverse(),
    ...bytes.slice(8)
  ])
}

// The federated credential Copilot Studio's threat detection signs in as the
// customer's app with; it names their Entra tenant and Kodem's endpoint
export const getThreatDetectionCredential = (
  azureTenantId: string,
  threatDetectionUrl: string
) => {
  if (!isGuid(azureTenantId) || !threatDetectionUrl) return undefined

  const tenant = azureTenantId.trim().toLowerCase()
  const endpoint = toBase64Url(new TextEncoder().encode(threatDetectionUrl))

  return {
    issuer: `${ENTRA_LOGIN_URL}/${tenant}/v2.0`,
    subject: `/eid1/c/pub/t/${toBase64Url(getGuidBytes(tenant))}/a/${COPILOT_STUDIO_APP}/${endpoint}`,
    audience: FEDERATED_AUDIENCE
  }
}

// An admin of the customer's tenant consents to Kodem's app once; any work
// account's tenant until the tenant id is known
export const getAdminConsentUrl = (appId: string, azureTenantId: string) => {
  const tenant = isGuid(azureTenantId) ? azureTenantId.trim() : "organizations"

  return `${ENTRA_LOGIN_URL}/${tenant}/adminconsent?client_id=${encodeURIComponent(appId)}`
}

// Without control the control tag is not sent
export const getAgentCoreSettingsRequest = (
  settings: AgentCoreSettings
): AgentCoreSettings => {
  const { regions, eventDiscovery, controlEnabled } = settings

  if (!controlEnabled) return { regions, eventDiscovery, controlEnabled }

  return {
    regions,
    eventDiscovery,
    controlEnabled,
    controlledRoleTag: (settings.controlledRoleTag || "").trim()
  }
}

export const isAgentCoreSettingsValid = (settings: AgentCoreSettings) =>
  settings.regions.length > 0 &&
  (!settings.controlEnabled || isRoleTag(settings.controlledRoleTag))

const sorted = (values: string[] | undefined) => [...(values || [])].sort()

// A settings change that needs a new CloudFormation template
export const isAgentCoreTemplateChanged = (
  previous: AgentCoreSettings | undefined,
  next: AgentCoreSettings
) => {
  const a = getAgentCoreSettingsRequest({
    ...DEFAULT_AGENTCORE_SETTINGS,
    ...previous
  })
  const b = getAgentCoreSettingsRequest(next)

  return (
    JSON.stringify({ ...a, regions: sorted(a.regions) }) !==
    JSON.stringify({ ...b, regions: sorted(b.regions) })
  )
}

const STATUS: Record<AgentPlatformStatus, ConnectionStatus> = {
  HEALTHY: { tKey: "healthy", color: "var(--polar-green-6-color)" },
  DEGRADED: { tKey: "unhealthy", color: "var(--sunset-orange-5-color)" },
  UNREACHABLE: { tKey: "unreachable", color: "var(--dust-red-5-color)" }
}

const isKnownStatus = (status?: string): status is AgentPlatformStatus =>
  !!status && Object.keys(STATUS).includes(status)

// The API leaves the status out while it is unspecified
const UNKNOWN_STATUS: ConnectionStatus = {
  tKey: "unknown",
  color: "var(--neutral-6-color)"
}

export const getConnectionStatus = (status: string | undefined) =>
  isKnownStatus(status) ? STATUS[status] : UNKNOWN_STATUS
